import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, Menu, X } from "lucide-react";
import Logo from "./Logo";
import { BUSINESS } from "../lib/site";

const NAV_LINKS = [
    { label: "Home", to: "/" },
    { label: "Pricing", to: "/pricing" },
    { label: "Jobs", to: "/gallery" },
    { label: "Free Quote", to: "/quote" },
];

const Navbar = () => {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const location = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        setOpen(false);
    }, [location.pathname]);

    return (
        <header
            className={`fixed top-0 inset-x-0 z-50 border-b transition-colors duration-300 ${
                scrolled
                    ? "bg-brand-ink/85 backdrop-blur-xl border-brand-line"
                    : "bg-brand-ink/40 backdrop-blur-md border-transparent"
            }`}
        >
            <div className="max-w-7xl mx-auto px-5 sm:px-8 h-[72px] flex items-center justify-between">
                <Link to="/" data-testid="nav-logo-link" aria-label="Southeast Stump Grinding home">
                    <Logo />
                </Link>

                <nav className="hidden md:flex items-center gap-8">
                    {NAV_LINKS.map((l) => (
                        <Link
                            key={l.to}
                            to={l.to}
                            data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                            className={`font-mono text-xs uppercase tracking-[0.2em] transition-colors hover:text-brand-amber ${
                                location.pathname === l.to
                                    ? "text-brand-amber"
                                    : "text-brand-sage"
                            }`}
                        >
                            {l.label}
                        </Link>
                    ))}
                    <a
                        href={BUSINESS.phoneTel}
                        data-testid="nav-phone-link"
                        className="inline-flex items-center gap-2 bg-brand-amber text-brand-ink font-semibold text-sm px-5 py-2.5 rounded-full hover:bg-brand-amberHover transition-colors"
                    >
                        <Phone className="w-4 h-4" />
                        Call {BUSINESS.phoneDisplay}
                    </a>
                </nav>

                <div className="flex items-center gap-3 md:hidden">
                    <a
                        href={BUSINESS.phoneTel}
                        data-testid="nav-phone-link-mobile"
                        aria-label={`Call ${BUSINESS.phoneDisplay}`}
                        className="inline-flex items-center gap-2 bg-brand-amber text-brand-ink font-semibold text-sm px-4 py-2 rounded-full"
                    >
                        <Phone className="w-4 h-4" />
                        Call
                    </a>
                    <button
                        data-testid="nav-mobile-toggle"
                        aria-label="Toggle menu"
                        onClick={() => setOpen((v) => !v)}
                        className="p-2 text-brand-cream"
                    >
                        {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                    </button>
                </div>
            </div>

            <AnimatePresence>
                {open && (
                    <motion.nav
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.25, ease: "easeOut" }}
                        className="md:hidden overflow-hidden bg-brand-ink/95 backdrop-blur-xl border-b border-brand-line"
                    >
                        <div className="px-5 py-4 flex flex-col gap-1">
                            {NAV_LINKS.map((l) => (
                                <Link
                                    key={l.to}
                                    to={l.to}
                                    data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                                    className={`py-3 font-display text-2xl font-bold uppercase tracking-wide ${
                                        location.pathname === l.to
                                            ? "text-brand-amber"
                                            : "text-brand-cream"
                                    }`}
                                >
                                    {l.label}
                                </Link>
                            ))}
                        </div>
                    </motion.nav>
                )}
            </AnimatePresence>
        </header>
    );
};

export default Navbar;
